/* eslint formatjs/no-literal-string-in-jsx: "error" */

import React, { useEffect } from "react"
import { FlexContainer, SlidingDotsLoader, Text } from "@carrotfertility/carotene"
import { ProviderDetails } from "./ProviderDetails"
import { Provider } from "../../types/Providers"
import { PaginationBar } from "./PaginationBar"
import { useHistory } from "react-router-dom"
import { getHeap } from "../../utils/heap"
import { ProviderFinderParam, providerFinderRootPath } from "../../utils/providerFinderSearchUrl"
import { HeapPropertyValue } from "utils/heap"
import { Benefit } from "pages/provider-finder/types/Benefit"
import { FormattedMessage } from "react-intl"
import { Box } from "@carrotfertility/carotene-core"

const ITEMS_PER_PAGE = 25

export function ResultList({ providers, benefit }: { providers: Provider[]; benefit: Benefit }): JSX.Element {
  const history = useHistory()

  const params = new URLSearchParams(history.location.search)
  const page = Number(params.get(ProviderFinderParam.Page))
  const searchLocation = params.get(ProviderFinderParam.Search)
  const carrotPartnersOnly = params.get(ProviderFinderParam.CarrotPartner) ? "true" : "false"

  const providerCount = providers.length
  const pageCount = Math.ceil(providerCount / ITEMS_PER_PAGE)

  const startIndex = (page - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE - 1
  const currentItems = providers.slice(startIndex, endIndex + 1)
  const carrotPartnerCount = currentItems.filter((p) => p.carrotPartner).length

  useEffect(
    function trackProviderSearchResultsRendered() {
      const customProperties: Record<string, HeapPropertyValue> = {
        "Provider Count": providerCount,
        "Carrot Partners Filter on (true/false)": carrotPartnersOnly,
        "Total Page Count": pageCount,
        "Results per Page": ITEMS_PER_PAGE,
        "Page Number": page
      }
      if (carrotPartnersOnly === "false") {
        customProperties["Carrot Partner Count"] = carrotPartnerCount
      }
      getHeap().track("Provider Search Results Rendered", customProperties)
    },
    [providerCount, carrotPartnersOnly, pageCount, carrotPartnerCount, page]
  )

  function handlePageChange(event: React.ChangeEvent, page: number): void {
    const params = new URLSearchParams(history.location.search)
    params.set(ProviderFinderParam.Page, page.toString())
    history.push(`${providerFinderRootPath}/providers?${params}`)
  }

  const firstPageResultRank = startIndex + 1
  const lastProviderRank = Math.min(endIndex + 1, providerCount)

  return (
    <FlexContainer direction="column" padding="0 0 96px 0">
      <Box
        sx={{ "> li::before": { display: "none" } }}
        component="ol"
        display="flex"
        flexDirection="column"
        gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
      >
        {currentItems && currentItems.length ? (
          currentItems.map((provider, index) => (
            <Box
              component="li"
              sx={{ borderBottom: "1px solid #dadce0", paddingBottom: "28px" }}
              key={provider.providerUuid}
            >
              <ProviderDetails
                provider={provider}
                rank={startIndex + index + 1}
                searchLocation={searchLocation}
                benefit={benefit}
              />
            </Box>
          ))
        ) : (
          <FlexContainer height="180px" justifyContent="center" alignItems="center">
            <SlidingDotsLoader />
          </FlexContainer>
        )}
      </Box>
      <FlexContainer justifyContent="center" width="100%" padding="12px 0 36px 0 ">
        <Text color="black40" size="12px">
          <FormattedMessage
            defaultMessage="{firstPageResultRank}-{lastProviderRank} of {providerCount}"
            values={{
              firstPageResultRank: firstPageResultRank,
              lastProviderRank: lastProviderRank,
              providerCount: providerCount
            }}
          />
        </Text>
      </FlexContainer>
      <PaginationBar count={pageCount} onChange={handlePageChange} page={page} />
    </FlexContainer>
  )
}
